import { Routes } from '@angular/router';
import { authGuard } from './guard/auth-guard';
import { unauthGuard } from './guard/unauth-guard';
import { adminPermissionGuard } from './guard/admin-permission-guard';
import { CoursePlayerExample } from './pages/course-player-example/course-player-example';
import { userResolver } from './pages/admin/user/user.resolver';
import { roleResolver } from './pages/admin/role/role.resolver';
import { totalCareIqResolver } from './pages/totalcareiq/totalcareiq.resolver';
import { seedResolver } from './pages/admin/seed/seed.resolver';
import { announcementResolver } from './pages/admin/announcements/announcement-page/announcement.resolver';
import { resourceResolver } from './pages/admin/resources/resource-page/resource.resolver';
import { tenantResolver } from './pages/admin/tenant/tenant.resolver';

export const routes: Routes = [
  {
    path: '',
    title: 'EDUCATIONiQ',
    loadComponent: () => import('./pages/home/home').then((m) => m.Home),
    canActivate: [authGuard],
  },
  {
    path: 'login',
    title: 'Login',
    loadComponent: () =>
      import('./pages/authentication/authentication').then((m) => m.Authentication),
    canActivate: [unauthGuard],
  },
  {
    path: 'logout',
    title: 'Logout',
    loadComponent: () => import('./pages/logout/logout').then((m) => m.Logout),
    canActivate: [authGuard],
  },
  {
    path: 'my-insights',
    title: 'My Insights',
    loadComponent: () => import('./pages/insights/insights').then((m) => m.Insights),
    canActivate: [authGuard],
  },
  {
    path: 'all/:type',
    loadComponent: () =>
      import('./pages/resources-search/resources-search').then((m) => m.ResourcesSearch),
    title: (route) => {
      const type = route.params['type'];
      const titleCase = type.replace(/[-_]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());

      return `${titleCase} Resources`;
    },
  },
  {
    path: 'learning-exchange',
    title: 'Learning Exchange',
    loadComponent: () =>
      import('./pages/learning-exchange/learning-exchange').then((m) => m.LearningExchange),
    canActivate: [authGuard],
  },
  { path: 'course-player-example', title: 'Course Player', component: CoursePlayerExample },
  {
    path: 'TotalCAREiQ',
    title: 'TotalCAREiQ',
    loadComponent: () => import('./pages/totalcareiq/totalcareiq').then((m) => m.Totalcareiq),
    canActivate: [authGuard],
    resolve: { initialData: totalCareIqResolver },
  },
  {
    path: 'my-profile',
    title: 'My Profile',
    loadComponent: () => import('./pages/admin/user/user').then((m) => m.User),
    resolve: { initialData: userResolver },
  },
  {
    path: 'admin',
    canActivateChild: [adminPermissionGuard()],
    children: [
      {
        path: 'seed',
        title: 'Seed',
        loadComponent: () => import('./pages/admin/seed/seed').then((m) => m.Seed),
        canActivate: [adminPermissionGuard('admin:data-sync:execute')],
        resolve: { formData: seedResolver },
      },
      {
        path: 'organization-settings',
        title: 'Organization Settings',
        loadComponent: () =>
          import('./pages/admin/organization-settings/organization-settings').then(
            (m) => m.OrganizationSettings,
          ),
        canActivate: [adminPermissionGuard('admin:tenant-settings:view')],
      },
      {
        path: 'users',
        title: 'Users',
        loadComponent: () =>
          import('./pages/admin/users-search/users-search').then((m) => m.UsersSearch),
        canActivate: [adminPermissionGuard('admin:users:view')],
      },
      {
        path: 'user/create',
        title: 'Create User',
        loadComponent: () => import('./pages/admin/user/user').then((m) => m.User),
        canActivate: [adminPermissionGuard('admin:users:view', 'admin:users:create')]
      },
      {
        path: 'user/:id',
        title: 'Edit User',
        loadComponent: () => import('./pages/admin/user/user').then((m) => m.User),
        canActivate: [adminPermissionGuard('admin:users:view', 'admin:users:edit')],
        resolve: { initialData: userResolver },
      },
      {
        path: 'roles',
        title: 'Roles',
        loadComponent: () =>
          import('./pages/admin/roles-search/roles-search').then((m) => m.RolesSearch),
        canActivate: [adminPermissionGuard('admin:roles:view')],
      },
      {
        path: 'role/create',
        title: 'Create Role',
        loadComponent: () => import('./pages/admin/role/role').then((m) => m.Role),
        canActivate: [adminPermissionGuard('admin:roles:view', 'admin:roles:create')]
      },
      {
        path: 'role/:id',
        title: 'Edit Role',
        loadComponent: () => import('./pages/admin/role/role').then((m) => m.Role),
        canActivate: [adminPermissionGuard('admin:roles:view', 'admin:roles:edit')],
        resolve: { initialData: roleResolver },
      },
      {
        path: 'tenants',
        title: 'Tenants',
        loadComponent: () =>
          import('./pages/admin/tenants-search/tenants-search').then((m) => m.TenantsSearch),
        canActivate: [adminPermissionGuard('admin:tenants:view')],
      },
      {
        path: 'tenant-courses',
        title: 'Tenant Courses',
        loadComponent: () =>
          import('./pages/admin/tenant-courses/tenant-courses').then((m) => m.TenantCourses),
        canActivate: [adminPermissionGuard('admin:tenant-courses:view')],
      },
      {
        path: 'tenant/create',
        title: 'Create Tenant',
        loadComponent: () => import('./pages/admin/tenant/tenant-page').then((m) => m.TenantPage),
        canActivate: [adminPermissionGuard('admin:tenants:view', 'admin:tenants:create')]
      },
      {
        path: 'tenant/:id',
        title: 'Edit Tenant',
        loadComponent: () => import('./pages/admin/tenant/tenant-page').then((m) => m.TenantPage),
        canActivate: [adminPermissionGuard('admin:tenants:view', 'admin:tenants:edit')],
        resolve: { initialData: tenantResolver }
      },
      {
        path: 'announcements',
        title: 'Announcements',
        loadComponent: () =>
          import('./pages/admin/announcements/announcements-search/announcements-search').then(
            (m) => m.AnnouncementsSearch
          ),
        canActivate: [adminPermissionGuard('admin:announcements:view')]
      },
      {
        path: 'announcement/create',
        title: 'Create Announcement',
        loadComponent: () =>
          import('./pages/admin/announcements/announcement-page/announcement-page').then((m) => m.AnnouncementPage),
        canActivate: [
          adminPermissionGuard('admin:announcements:view', 'admin:announcements:create')
        ]
      },
      {
        path: 'announcement/:id',
        title: 'Edit Announcement',
        loadComponent: () =>
          import('./pages/admin/announcements/announcement-page/announcement-page').then((m) => m.AnnouncementPage),
        canActivate: [
          adminPermissionGuard('admin:announcements:view', 'admin:announcements:edit')
        ],
        resolve: { initialData: announcementResolver }
      },
      {
        path: 'resources',
        title: 'Resources',
        loadComponent: () =>
          import('./pages/admin/resources/resources-search/resources-search').then(
            (m) => m.ResourcesSearch
          ),
        canActivate: [adminPermissionGuard('admin:resources:view')]
      },
      {
        path: 'resource/create',
        title: 'Create Resource',
        loadComponent: () => import('./pages/admin/resources/resource-page/resource-page').then((m) => m.ResourcePage),
        canActivate: [adminPermissionGuard('admin:resources:view', 'admin:resources:create')],
      },
      {
        path: 'resource/:id',
        title: 'Edit Resource',
        loadComponent: () => import('./pages/admin/resources/resource-page/resource-page').then((m) => m.ResourcePage),
        canActivate: [adminPermissionGuard('admin:resources:view', 'admin:resources:edit')],
        resolve: { initialData: resourceResolver }
      }
    ]
  },
  {
    path: '**',
    title: 'EMS | Fallback',
    loadComponent: () => import('./pages/fallback/fallback').then((m) => m.Fallback),
  },
];
